import React from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";

import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { makeStyles } from "@material-ui/core/styles";

import {
  ADM_AREAS_CORRECTION_CREATE_URL,
  ADM_AREAS_CORRECTION_EDIT_URL
} from "../../config";
import SubmitButton from "../button/SubmitButton";
import CancelButton from "../button/CancelButton";
import TextField from "../formik/TextField";
import {
  showSuccessAlert,
  showErrorAlert,
  showWarningAlert
} from "../../store/actions/alerts";

import makeApiRequest from "../../utils/axios/new-axios";

const useStyles = makeStyles(theme => ({
  form: {
    display: "flex",
    flexDirection: "column",
    margin: "auto",
    width: "fit-content"
  }
}));

const yupSchema = Yup.object().shape({
  country: Yup.string().nullable(),
  countryAlt: Yup.string().nullable(),
  adm1: Yup.string().nullable(),
  adm1Alt: Yup.string().nullable(),
  adm2: Yup.string().nullable(),
  adm2Alt: Yup.string().nullable(),
  adm3: Yup.string().nullable(),
  adm3Alt: Yup.string().nullable()
});

export default function AddEditCorrectionForm({
  openModal,
  handleCloseModal,
  initialValues,
  action,
  parentId,
  correctionId,
  updateCorrectionTable
}) {
  const dispatch = useDispatch();
  const classes = useStyles();
  //const [disableModalDismiss, setDisableModalDismiss] = useState(false);

  async function handleSubmit(values, { setSubmitting }) {
    setSubmitting(true);
    //setDisableModalDismiss(true);

    const reqJson = {
      country: {
        name: values.country.trim().length > 0 ? values.country.trim() : null,
        nameAlt:
          values.countryAlt.trim().length > 0 ? values.countryAlt.trim() : null
      },
      adm1: {
        name: values.adm1.trim().length > 0 ? values.adm1.trim() : null,
        nameAlt: values.adm1Alt.trim().length > 0 ? values.adm1Alt.trim() : null
      },
      adm2: {
        name: values.adm2.trim().length > 0 ? values.adm2.trim() : null,
        nameAlt: values.adm2Alt.trim().length > 0 ? values.adm2Alt.trim() : null
      },
      adm3: {
        name: values.adm3.trim().length > 0 ? values.adm3.trim() : null,
        nameAlt: values.adm3Alt.trim().length > 0 ? values.adm3Alt.trim() : null
      }
    };

    let countNulls = 0;
    !reqJson.country.name && countNulls++;
    !reqJson.adm1.name && countNulls++;
    !reqJson.adm2.name && countNulls++;
    !reqJson.adm3.name && countNulls++;

    if (countNulls <= 2) {
      await makeApiRequest({
        url:
          action === "add"
            ? `${ADM_AREAS_CORRECTION_CREATE_URL.url}/${parentId}`
            : `${ADM_AREAS_CORRECTION_EDIT_URL.url}/${correctionId}`,
        method:
          action === "add"
            ? ADM_AREAS_CORRECTION_CREATE_URL.method
            : ADM_AREAS_CORRECTION_EDIT_URL.method,
        data: reqJson
      })
        .then(response => {
          //console.log(response);
          if (action === "add") {
            updateCorrectionTable(
              action,
              {
                ...reqJson,
                id: response.data.id
              },
              null
            );
            dispatch(showSuccessAlert("Added Successfully"));
          } else {
            if (response.data.affectedRows > 0) {
              updateCorrectionTable(
                action,
                {
                  ...reqJson,
                  id: response.data.id
                },
                correctionId
              );
              dispatch(showSuccessAlert(`Updated Successfully!`));
            } else {
              dispatch(showWarningAlert(`Nothing updated. But no errors!`));
            }
          }
          handleCloseModal();
        })
        .catch(error => {
          dispatch(
            showErrorAlert(
              "Error : " +
              (!!error.response
                ? `${error.response.status}! - ${error.response.data.errorMessage}`
                : error.message)
            )
          );
        });
    } else {
      dispatch(
        showErrorAlert(
          "At least 2 of Country, Adm1 Name, Adm 2 Name or Adm 3 Name is required"
        )
      );
    }

    setSubmitting(false);
    //setDisableModalDismiss(false);
  }

  return (
    <Dialog
      open={openModal}
      onClose={handleCloseModal}
      fullWidth={true}
      maxWidth="sm"
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      disableBackdropClick={true}
      disableEscapeKeyDown={true}
    >
      <DialogTitle id="alert-dialog-title">
        {action === "add" ? "Add" : "Edit"} Administrative Area Correction
      </DialogTitle>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={yupSchema}
      >
        {props => {
          const { isSubmitting } = props;
          return (
            <Form className={classes.form}>
              <DialogContent dividers>
                <Field
                  component={TextField}
                  name="country"
                  margin="dense"
                  label="Country Name"
                  fullWidth
                  disabled={isSubmitting}
                  autoFocus
                />
                <Field
                  component={TextField}
                  name="countryAlt"
                  margin="dense"
                  label="Country Short Name"
                  fullWidth
                  disabled={isSubmitting}
                />
                <Field
                  component={TextField}
                  name="adm1"
                  margin="dense"
                  label="Adm 1 Name"
                  fullWidth
                  disabled={isSubmitting}
                />
                <Field
                  component={TextField}
                  name="adm1Alt"
                  margin="dense"
                  label="Adm 1 Short Name"
                  fullWidth
                  disabled={isSubmitting}
                />
                <Field
                  component={TextField}
                  name="adm2"
                  margin="dense"
                  label="Adm 2 Name"
                  fullWidth
                  disabled={isSubmitting}
                />
                <Field
                  component={TextField}
                  name="adm2Alt"
                  margin="dense"
                  label="Adm 2 Short Name"
                  fullWidth
                  disabled={isSubmitting}
                />
                <Field
                  component={TextField}
                  name="adm3"
                  margin="dense"
                  label="Adm 3 Name"
                  fullWidth
                  disabled={isSubmitting}
                />
                <Field
                  component={TextField}
                  name="adm3Alt"
                  margin="dense"
                  label="Adm 3 Short Name"
                  fullWidth
                  disabled={isSubmitting}
                />
              </DialogContent>
              <DialogActions style={{ margin: "20px" }}>
                <CancelButton
                  onClick={handleCloseModal}
                  disabled={isSubmitting}
                >
                  Cancel
                </CancelButton>
                <SubmitButton
                  disabled={isSubmitting}
                  isSubmitting={isSubmitting}
                  autoFocus
                >
                  {action === "add" ? "Add" : "Update"}
                </SubmitButton>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
}
